import React from "react";
import { Button, Typography, Space, Steps } from "antd";
import { SafetyCertificateOutlined, CrownOutlined, CheckCircleFilled, ClockCircleOutlined } from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { localData } from "@/services/helper";
import { ACTIVE_SUBSCRIPTION_STATUS, EXPIRED_SUBSCRIPTION_STATUS, KYC_PROTECTED_ROUTES } from "@/utils/constants";
import {role} from "@/utils/constants";

const { Title, Paragraph, Text } = Typography;

interface KycGuardProps {
    children: React.ReactNode;
}


const KycGuard: React.FC<KycGuardProps> = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();

    const authData: any = localData.get("AUTHDATA");
    const userRole = authData?.role?.toLowerCase();
    const isSuperAdmin = userRole === role.SUPER_ADMIN_ROLE;
    const isStudioAdmin = userRole === role.STUDIO_ADMIN_ROLE;
    const isFreelancePhotographer = userRole === role.FREELANCE_PHOTOGRAPHER_ROLE;

    const canManageSubscription = isStudioAdmin || isFreelancePhotographer;

    if (isSuperAdmin) return <>{children}</>;

    const requiresKyc = KYC_PROTECTED_ROUTES.some(
        (route) => location.pathname === route || location.pathname.startsWith(route + "/")
    );

    if (!requiresKyc) return <>{children}</>;

    const isKycVerified = authData?.kycVerification?.isVerified === true;
    const isSubscribed = ACTIVE_SUBSCRIPTION_STATUS.includes(authData?.subscriptionStatus);
    const isExpired = EXPIRED_SUBSCRIPTION_STATUS.includes(authData?.subscriptionStatus);

    if (!canManageSubscription) {
        if (isKycVerified) return <>{children}</>;
    } else {
        if (isKycVerified && isSubscribed) return <>{children}</>;
    }

    const getScreenType = () => {
        if (!isKycVerified) return "kyc";
        if (isExpired) return "expired";
        return "subscribe";
    };
    const screenType = getScreenType();

    const screenConfig = {
        kyc: {
            icon: <SafetyCertificateOutlined style={{ fontSize: 72, color: "#1677ff", marginBottom: 20 }} />,
            title: "Aadhaar Verification Required",
            description: "Your account has been created successfully, but Aadhaar verification is still pending.",
            body: "Complete your Aadhaar verification to proceed to the next step and unlock all platform features.",
            footer: "Verification usually takes less than a minute.",
            actions: (
                <Space size="middle">
                    <Button size="large" onClick={() => navigate(-1)}>Go Back</Button>
                    <Button type="primary" size="large" onClick={() => navigate("/profile")}>Verify Aadhaar</Button>
                </Space>
            ),
        },
        subscribe: {
            icon: <CrownOutlined style={{ fontSize: 72, color: "#faad14", marginBottom: 20 }} />,
            title: "Subscribe to a Plan",
            description: "Great! Your Aadhaar has been verified successfully.",
            body: "Choose a subscription plan to unlock all features and start using the platform.",
            footer: "Pick a plan that best suits your needs.",
            actions: (
                <Space size="middle">
                    <Button size="large" onClick={() => navigate(-1)}>Go Back</Button>
                    <Button type="primary" size="large" icon={<CrownOutlined />} onClick={() => navigate("/subscription")}>View Plans</Button>
                </Space>
            ),
        },
        expired: {
            icon: <CrownOutlined style={{ fontSize: 72, color: "#ff4d4f", marginBottom: 20 }} />,
            title: "Subscription Expired",
            description: "Your subscription has expired and access to platform features has been restricted.",
            body: "Renew or choose a new subscription plan to continue using the platform without interruption.",
            footer: "Your data is safe. Renew anytime to regain access.",
            actions: (
                <Space size="middle">
                    <Button size="large" onClick={() => navigate(-1)}>Go Back</Button>
                    <Button type="primary" danger size="large" icon={<CrownOutlined />} onClick={() => navigate("/subscription")}>Renew Subscription</Button>
                </Space>
            ),
        },
    };

    const screen = screenConfig[screenType];
    const showSteps = screenType !== "expired";
    console.log("screenType", screenType, "isKycVerified", isKycVerified, "isSubscribed", isSubscribed, "isExpired", isExpired);
    const currentStep = !isKycVerified ? 0 : 1;

    const steps = [
        {
            title: "Aadhaar Verification",
            icon: isKycVerified
                ? <CheckCircleFilled style={{ color: "#52c41a" }} />
                : <ClockCircleOutlined style={{ color: "#1677ff" }} />,
        },
        ...(canManageSubscription ? [{
            title: "Subscribe to a Plan",
            icon: isSubscribed
                ? <CheckCircleFilled style={{ color: "#52c41a" }} />
                : <ClockCircleOutlined style={{ color: currentStep === 1 ? "#1677ff" : "#d9d9d9" }} />,
        }] : []),
    ];

    return (
        <div style={{ position: "relative", width: "100%", height: "100%", minHeight: "100%" }}>
            <div style={{ filter: "blur(8px)", pointerEvents: "none", userSelect: "none", height: "100%" }}>
                {children}
            </div>
            <div style={{ position: "absolute", inset: 0, background: "rgba(255,255,255,0.75)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000, padding: 24 }}>
                <div style={{ width: 560, maxWidth: "100%", background: "#fff", borderRadius: 20, padding: 40, boxShadow: "0 24px 80px rgba(0,0,0,0.15)", textAlign: "center" }}>
                    {screen.icon}
                    {showSteps && <Steps current={currentStep} items={steps} style={{ marginBottom: 30 }} size="small" />}
                    <Title level={2} style={{ marginBottom: 10 }}>{screen.title}</Title>
                    <Paragraph type="secondary" style={{ fontSize: 16, lineHeight: 1.7, marginBottom: 16 }}>{screen.description}</Paragraph>
                    <Paragraph style={{ fontSize: 15, marginBottom: 30 }}>{screen.body}</Paragraph>
                    {screen.actions}
                    <div style={{ marginTop: 24 }}>
                        <Text type="secondary">{screen.footer}</Text>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default KycGuard;